/**
 * Utility functions for forecast calculations based on driver types
 */

/**
 * Calculate forecast amount based on driver type
 * @param {Object} params - Calculation parameters
 * @param {string} params.driverType - The forecast driver type
 * @param {number} params.input - The driver input value
 * @param {number} params.budget - Budget amount for the month
 * @param {number} params.actual - Actual amount for the month
 * @param {number} params.availableRooms - Available rooms for the month (to date)
 * @param {number} params.occupiedRooms - Occupied rooms for the month (to date)
 * @param {number} params.previousMonthActual - Previous month's actual amount
 * @param {number} params.previousMonthForecast - Previous month's forecast amount
 * @param {number} params.previousMonthAvailableRooms - Previous month's available rooms
 * @param {number} params.previousMonthOccupiedRooms - Previous month's occupied rooms
 * @param {number} params.totalRevenue - Total revenue for the month (for % of Revenue)
 * @param {number} params.daysWithData - Number of days with room data (for pace calculation)
 * @param {number} params.daysInMonth - Total number of days in the month
 * @returns {number} - The calculated forecast amount
 */
export const calculateForecastAmount = ({
  driverType,
  input,
  budget = 0,
  actual = 0,
  availableRooms = 0,
  occupiedRooms = 0,
  previousMonthActual = 0,
  previousMonthForecast = 0,
  previousMonthAvailableRooms = 0,
  previousMonthOccupiedRooms = 0,
  totalRevenue = 0,
  daysWithData = 0,
  daysInMonth = 30
}) => {
  // Parse input as a number
  const inputValue = parseFloat(input) || 0;

  switch (driverType) {
    case '':
      // Empty driver type - user enters forecast manually
      // Return null to indicate no calculation
      return null;

    case 'Static Entry':
      // Static Entry - use saved forecast amount from database
      // Return null to indicate no calculation needed
      return null;

    case 'Budget':
      // Use budget amount
      return budget;

    case '$ / ARN':
      // Dollar per Available Room Night
      // Use pace-based projection: (rooms to date / days elapsed) × days in month × driver input
      if (daysWithData > 0 && availableRooms > 0) {
        const avgDailyRooms = availableRooms / daysWithData;
        const projectedTotalRooms = avgDailyRooms * daysInMonth;
        return inputValue * projectedTotalRooms;
      }
      // Fallback to simple multiplication if no days data
      return inputValue * availableRooms;

    case '$ / ORN':
      // Dollar per Occupied Room Night
      // Use pace-based projection: (occupied rooms to date / days elapsed) × days in month × driver input
      if (daysWithData > 0 && occupiedRooms > 0) {
        const avgDailyOccupied = occupiedRooms / daysWithData;
        const projectedTotalOccupied = avgDailyOccupied * daysInMonth;
        return inputValue * projectedTotalOccupied;
      }
      // Fallback to simple multiplication if no days data
      return inputValue * occupiedRooms;

    case 'REVPAR Projection':
      // Revenue Per Available Room projection
      // Input is the target REVPAR
      // Formula: input × availableRooms
      return inputValue * availableRooms;

    case '% of Revenue':
      // Percentage of total revenue
      // Input is stored as percentage (e.g., 5 for 5%)
      // Formula: totalRevenue × (input / 100)
      return totalRevenue * (inputValue / 100);

    case 'Occupancy Forecast':
      // Based on occupancy rate and input per occupied room
      // Formula: (occupiedRooms / availableRooms) × availableRooms × input
      // Simplifies to: occupiedRooms × input
      if (availableRooms === 0) return 0;
      return occupiedRooms * inputValue;

    case '$ LM':
      // Dollar amount from Last Month
      // Use previous month's actual, or fallback to previous forecast
      return previousMonthActual || previousMonthForecast || 0;

    case '$ / ARN LM':
      // Dollar per Available Room Night - Last Month
      // Calculate last month's rate and apply to current month
      // Formula: (Last Month Actual / Last Month Available Rooms) × Current Month Available Rooms
      if (previousMonthAvailableRooms > 0 && availableRooms > 0) {
        const lastMonthARNRate = previousMonthActual / previousMonthAvailableRooms;
        return lastMonthARNRate * availableRooms;
      }
      return 0;

    case '$ / ORN LM':
      // Dollar per Occupied Room Night - Last Month
      // Calculate last month's rate and apply to current month
      // Formula: (Last Month Actual / Last Month Occupied Rooms) × Current Month Occupied Rooms
      if (previousMonthOccupiedRooms > 0 && occupiedRooms > 0) {
        const lastMonthORNRate = previousMonthActual / previousMonthOccupiedRooms;
        return lastMonthORNRate * occupiedRooms;
      }
      return 0;

    case 'OTB Forecast':
      // On The Books Forecast Revenue
      // This value comes from the SQL view, no calculation needed
      return null;

    case 'RMS Forecast':
      // Revenue Management System Forecast Revenue
      // This value comes from the SQL view, no calculation needed
      return null;

    default:
      return null;
  }
};

/**
 * Check if a driver type requires user input
 * @param {string} driverType - The forecast driver type
 * @returns {boolean} - Whether the driver requires input
 */
export const driverRequiresInput = (driverType) => {
  const requiresInput = ['$ / ORN', '$ / ARN', '% of Revenue', 'REVPAR Projection', 'Occupancy Forecast', 'Static Entry'];
  return requiresInput.includes(driverType);
};

/**
 * Check if a driver type uses automatic calculation
 * @param {string} driverType - The forecast driver type
 * @returns {boolean} - Whether the driver uses auto-calculation
 */
export const isAutoCalculatedDriver = (driverType) => {
  // Static Entry now auto-copies from input, so it's considered auto-calculated
  // Empty string ('') is the only type that allows direct forecast entry
  return driverType !== '';
};

/**
 * Get previous month's index
 * @param {number} monthIndex - Current month index (0-11)
 * @returns {number} - Previous month index (0-11)
 */
export const getPreviousMonthIndex = (monthIndex) => {
  return monthIndex === 0 ? 11 : monthIndex - 1;
};

/**
 * Format driver input for display based on driver type
 * @param {string} driverType - The forecast driver type
 * @param {number} input - The input value
 * @returns {string} - Formatted input display
 */
export const formatDriverInput = (driverType, input) => {
  const inputValue = parseFloat(input) || 0;

  switch (driverType) {
    case '% of Revenue':
      // Input is stored as percentage (e.g., 5 for 5%), display as-is with % sign
      return `${inputValue}%`;

    case '$ / ARN':
    case '$ / ORN':
    case 'REVPAR Projection':
    case 'Occupancy Forecast':
    case '$ LM':
      return `$${inputValue.toFixed(2)}`;

    default:
      return inputValue.toString();
  }
};

/**
 * Get helper text for a driver type
 * @param {string} driverType - The forecast driver type
 * @returns {string} - Helper text explaining the driver
 */
export const getDriverHelperText = (driverType) => {
  switch (driverType) {
    case '$ / ARN':
      return 'Forecast = (Avg Daily Rooms × Days in Month) × Input';

    case '$ / ORN':
      return 'Forecast = (Avg Daily Occupied × Days in Month) × Input';

    case 'Budget':
      return 'Forecast = Budget Amount';

    case 'REVPAR Projection':
      return 'Forecast = REVPAR × Available Rooms';

    case '% of Revenue':
      return 'Forecast = Total Revenue × Percentage';

    case 'Occupancy Forecast':
      return 'Forecast = Occupied Rooms × Input';

    case '$ LM':
      return 'Forecast = Previous Month Actual/Forecast';

    case 'Static Entry':
      return 'Forecast = Input Value (enter in Input column)';

    case 'OTB Forecast':
      return 'Forecast = On The Books Revenue from Hotel System';

    case 'RMS Forecast':
      return 'Forecast = Revenue Management System Projection';

    case '':
      return 'Enter forecast values manually';

    default:
      return '';
  }
};
